import {
  Field as IField,
  SECTION_BREAK,
  SECTION_BREAK_SMALL,
  AdditionalPassengersData,
  FieldType,
  FieldVisibilityRuleType,
  HeadingLevel,
  CreateDocResponse,
} from "@/core/types/types";
import { Box, Card } from "@mui/material";
import Field from "@/core/components/Field";
import { useGetPreviousAnswers } from "@/core/hooks/useGetPreviousAnswers/useGetPreviousAnswers";
import { useEffect } from "react";
import { signatureApi } from "@/api/signatureApi";
import { TextVariant } from "@/core/components/Field/Text";
import { AlreadySignedMessage } from "./AlreadySignedMessage";
import { FieldValues, useFieldArray, useFormContext } from "react-hook-form";
import {
  ADDITIONAL_PASSENGER_SIGNATURES_KEY,
  PASSENGER_AVAILABLE_KEY,
  SAME_ADDRESS_KEY,
} from "@/core/data";
interface RequiredVariablesResult {
  result: {
    createDocResponse: CreateDocResponse;
  };
  loading: boolean;
}

const AdditionalPassengerSignatures = ({
  groupItems,
}: {
  groupItems: IField[];
}) => {
  const [mainField, ...groupFields] = groupItems;
  const {
    properties: { requiredInputVariables },
  } = mainField;
  const { clearErrors, control, reset, getValues } = useFormContext();

  const { fields, append } = useFieldArray({
    name: ADDITIONAL_PASSENGER_SIGNATURES_KEY,
    control,
  });

  if (!requiredInputVariables) {
    throw new Error("Missing required input variable names.");
  }

  const { result, loading }: RequiredVariablesResult = useGetPreviousAnswers(
    requiredInputVariables
  );

  const documentId: string | null | undefined = result.createDocResponse?.id;

  const requestAdditionalPassengersData = async (documentId: string) => {
    const response: Promise<{ passengers: AdditionalPassengersData[] }> =
      signatureApi.getPassengersDataByDocumentId(documentId).then((res) => {
        return res;
      });
    const { passengers } = await response;

    const valuesToClearForm: FieldValues = getValues();
    reset({
      ...valuesToClearForm,
      additionalPassengerSignatures: [],
    });

    const [leadCustomer, ...additionalPassengers] = passengers;
    append([
      leadCustomer,
      ...(additionalPassengers ? [...additionalPassengers].reverse() : []),
    ]);
  };

  const handleRadioButtonChange = (key: string): void => {
    // Trim the main keyName with an index and clear the left errors
    const trimmedKeyName: string = key.replace(/\..*/g, "");
    clearErrors(trimmedKeyName);
  };

  useEffect(() => {
    if (documentId) {
      requestAdditionalPassengersData(documentId);
    }
  }, [documentId]);

  return (
    <>
      {!loading && fields.length ? (
        <>
          {fields.map(
            (
              passenger: AdditionalPassengersData | Record<"id", string>,
              idx: number
            ) => {
              const { firstName, lastName, signature } =
                passenger as AdditionalPassengersData;
              const fullName: string = `${firstName} ${lastName}`;
              const isAlreadySigned: boolean =
                !!signature?.base64EncodedSignature;

              // Hide main (first) passenger signature data by default
              if (idx === 0) return null;
              return (
                <Box key={idx}>
                  {!isAlreadySigned ? (
                    <Card
                      sx={{
                        p: 2,
                        pb: 1,
                        boxShadow: "none",
                        mb: 3,
                      }}
                    >
                      <Box>
                        <TextVariant
                          text={fullName}
                          variant={HeadingLevel.H3}
                        />
                        {groupFields.map((field: IField, index: number) => {
                          const fieldKey: string = `${ADDITIONAL_PASSENGER_SIGNATURES_KEY}[${idx}].${field.key}`;
                          const hideIfCondition: string | undefined =
                            field.properties?.[FieldVisibilityRuleType.HideIf];
                          const isRequired: boolean =
                            field.properties?.required || false;
                          const isSectionBreak: boolean = !!(field as IField)
                            .properties?.[SECTION_BREAK];
                          const isSmallSectionBreak: boolean = !!(
                            field as IField
                          ).properties?.[SECTION_BREAK_SMALL];
                          const isLastField: boolean =
                            index === groupFields.length - 1;
                          const isRadio: boolean =
                            field.type === FieldType.Radio;
                          const isAddressField: boolean =
                            field.key?.indexOf("address") === 0;
                          const modifiedHideIf: string | undefined =
                            !!hideIfCondition
                              ? hideIfCondition
                                  .replaceAll(
                                    PASSENGER_AVAILABLE_KEY,
                                    `${ADDITIONAL_PASSENGER_SIGNATURES_KEY}[${idx}].${PASSENGER_AVAILABLE_KEY}`
                                  )
                                  .replaceAll(
                                    SAME_ADDRESS_KEY,
                                    `${ADDITIONAL_PASSENGER_SIGNATURES_KEY}[${idx}].${SAME_ADDRESS_KEY}`
                                  )
                              : undefined;
                          return (
                            <Box
                              key={field.id}
                              mt={isAddressField ? 0 : 2}
                              mb={
                                isSectionBreak || isSmallSectionBreak
                                  ? 3
                                  : isLastField
                                  ? 2
                                  : 0
                              }
                            >
                              <Field
                                field={{
                                  ...field,
                                  key: fieldKey,
                                  validate: {
                                    required: isRequired,
                                  },
                                  properties: {
                                    ...field.properties,
                                    errorTranslationKey: field.key,
                                    [FieldVisibilityRuleType.HideIf]:
                                      modifiedHideIf,
                                  },
                                }}
                                onChange={
                                  isRadio ? handleRadioButtonChange : undefined
                                }
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    </Card>
                  ) : (
                    <AlreadySignedMessage fullName={fullName} />
                  )}
                </Box>
              );
            }
          )}
        </>
      ) : null}
    </>
  );
};

export { AdditionalPassengerSignatures };
